import { useState, useRef } from 'react';
import { Server, ShieldCheck, FileLock, Shield, Check, Loader2, CalendarClock, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';

const roadmapSteps = [
  { label: 'Internal Gap Assessment', status: 'completed' as const },
  { label: 'Implementation of HITRUST Control Set', status: 'active' as const },
  { label: 'External 3rd Party Validation', detail: 'Scheduled Q4 2026', status: 'upcoming' as const },
];

const defensiveLayers = [
  { icon: Server, title: 'Infrastructure Hardening', body: 'Hosted on HIPAA-hardened AWS/Azure environments with 99.99% uptime and geographically redundant backups.' },
  { icon: ShieldCheck, title: 'The Encryption Standard', body: 'AES-256 and TLS 1.3 encryption protocols protect every clinical intent from the OR to the Export.' },
  { icon: FileLock, title: 'Immutable Auditing', body: 'Every automated CPT assignment is logged in a tamper-proof ledger for permanent audit defensibility.' },
];

function StepIcon({ status }: { status: 'completed' | 'active' | 'upcoming' }) {
  if (status === 'completed')
    return (
      <div className="w-9 h-9 rounded-full bg-primary/15 border-2 border-primary flex items-center justify-center">
        <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
      </div>
    );
  if (status === 'active')
    return (
      <div className="w-9 h-9 rounded-full bg-cta/15 border-2 border-cta flex items-center justify-center">
        <Loader2 className="h-4 w-4 text-cta animate-spin" strokeWidth={2} />
      </div>
    );
  return (
    <div className="w-9 h-9 rounded-full bg-muted border-2 border-border flex items-center justify-center">
      <CalendarClock className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
    </div>
  );
}

export function TrustComplianceSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const [showOverlay, setShowOverlay] = useState(true);
  const [showTapToUnmute, setShowTapToUnmute] = useState(false);

  const startPlayback = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    videoRef.current.volume = volume;
    const p = videoRef.current.play();
    if (p) p.catch(() => {
      if (videoRef.current) { videoRef.current.muted = true; videoRef.current.play(); setShowTapToUnmute(true); setIsMuted(true); }
    });
    setShowOverlay(false);
    setIsPlaying(true);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) { videoRef.current.pause(); setIsPlaying(false); } else { startPlayback(); }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const m = !isMuted;
    videoRef.current.muted = m;
    if (!m) videoRef.current.volume = volume;
    setIsMuted(m);
    setShowTapToUnmute(false);
  };

  const handleVolumeChange = (val: number[]) => {
    if (!videoRef.current) return;
    const v = val[0];
    setVolume(v);
    videoRef.current.volume = v;
    if (v === 0) { videoRef.current.muted = true; setIsMuted(true); }
    else if (isMuted) { videoRef.current.muted = false; setIsMuted(false); }
    setShowTapToUnmute(false);
  };

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-14 md:mb-16 max-w-3xl mx-auto">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
            Trust & Compliance
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
            Enterprise Trust. Surgical Integrity.
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
            LINK isn't just a revenue tool; it's a security-first infrastructure designed to
            exceed the compliance requirements of the nation's largest healthcare systems.
          </p>
        </div>

        {/* Video */}
        <div className="mx-auto w-full max-w-4xl mb-14 md:mb-16">
          <div className="relative aspect-video rounded-xl border border-border overflow-hidden bg-foreground group">
            <video
              ref={videoRef}
              playsInline
              preload="metadata"
              className="w-full h-full object-contain"
              src="/videos/cybersecurity-infrastructure.mp4"
              onPlay={() => { setIsPlaying(true); setShowOverlay(false); }}
              onPause={() => setIsPlaying(false)}
              onEnded={() => { setIsPlaying(false); setShowOverlay(true); }}
            />
            {showTapToUnmute && !showOverlay && (
              <button
                onClick={() => { if (videoRef.current) { videoRef.current.muted = false; videoRef.current.volume = volume; setIsMuted(false); setShowTapToUnmute(false); } }}
                className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-2 rounded-md bg-card border border-border text-primary text-xs font-medium min-h-[44px]"
              >
                <VolumeX className="h-4 w-4" />
                Tap to Unmute
              </button>
            )}
            {showOverlay && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer" onClick={startPlayback}>
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-cta/20 border border-cta/40 flex items-center justify-center hover:scale-110 transition-transform">
                  <Play className="h-9 w-9 md:h-10 md:w-10 text-white ml-1" strokeWidth={1.5} />
                </div>
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 p-3 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={togglePlay} className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
              </button>
              <button onClick={toggleMute} className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <Slider value={[isMuted ? 0 : volume]} max={1} step={0.05} onValueChange={handleVolumeChange}
                className="w-24 [&_[data-radix-slider-track]]:bg-white/20 [&_[data-radix-slider-range]]:bg-white [&_[data-radix-slider-thumb]]:border-white [&_[data-radix-slider-thumb]]:bg-white" />
            </div>
          </div>
        </div>

        {/* HITRUST Roadmap */}
        <div className="max-w-3xl mx-auto mb-14 md:mb-16">
          <Card className="bg-card border border-border rounded-xl">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-foreground mb-6">
                The Road to HITRUST CSF Certification
              </h3>
              <div className="space-y-0">
                {roadmapSteps.map((step, idx) => (
                  <div key={step.label} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <StepIcon status={step.status} />
                      {idx < roadmapSteps.length - 1 && (
                        <div className={`w-0.5 flex-1 my-1 ${step.status === 'completed' ? 'bg-primary/40' : 'bg-border'}`} />
                      )}
                    </div>
                    <div className={`pb-6 ${idx === roadmapSteps.length - 1 ? 'pb-0' : ''}`}>
                      <p className={`text-sm font-semibold tracking-tight ${step.status === 'completed' ? 'text-primary' : step.status === 'active' ? 'text-cta' : 'text-muted-foreground'}`}>
                        {step.label}
                      </p>
                      {step.detail && <p className="text-xs text-muted-foreground mt-0.5">{step.detail}</p>}
                      {step.status === 'completed' && <p className="text-xs text-primary/70 mt-0.5">Completed</p>}
                      {step.status === 'active' && <p className="text-xs text-cta/70 mt-0.5">In Progress</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Defensive Layers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {defensiveLayers.map((layer) => (
            <Card key={layer.title} className="bg-card border border-border rounded-xl transition-all hover:shadow-lg">
              <CardContent className="p-8 space-y-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 border border-primary/20">
                  <layer.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-semibold text-foreground">{layer.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{layer.body}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
